import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Landing.css";

const LOGO_SRC = "https://customer-assets-v7afamib.emergentagent.net/job_3ded9da4-7891-48ae-9b29-bf144d630c6e/artifacts/lxcod03x_1000027137.png";
const FOUNDER_SRC = "https://customer-assets-v7afamib.emergentagent.net/job_3ded9da4-7891-48ae-9b29-bf144d630c6e/artifacts/r6bjwxv6_1000027136.jpg";

const CAPABILITIES = [
  { icon: "🎯", name: "Market Signal Engine", desc: "22 live buying signals swept every cycle — hiring moves, funding, expansion, leadership change. You know before anyone else." },
  { icon: "📇", name: "Prospect Intelligence", desc: "Find the decision maker. Reveal their email and direct line. Build your contact list without leaving the platform." },
  { icon: "📄", name: "AI Vision Decks", desc: "One click builds a premium, personalized proposal — their name on the cover, your brand, ready in minutes." },
  { icon: "☎️", name: "Call Brief & Script", desc: "A full call brief for every deal — custom script, editable, with the most common rebuttals and exactly how to answer them." },
  { icon: "📤", name: "Send & Track", desc: "Single or mass send your proposal — then watch every open and click land in real time." },
  { icon: "📊", name: "Executive Forecast Brief", desc: "One click builds a full pipeline forecast, ready to hand your team." },
  { icon: "🔁", name: "Built-In Retargeting", desc: "Your prospect gets reminded of your beautiful proposal — whether they open your email or not." },
  { icon: "🏗️", name: "Industry Architect", desc: "Set your business identity once. Every signal, proposal, and email reflects exactly who you sell to. No prompt engineering." },
];

export default function Landing() {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  useEffect(() => {
    if (!gridRef.current) return;
    const cards = gridRef.current.querySelectorAll(".cap-card");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => entry.target.classList.add("visible"), i * 60);
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    cards.forEach((c) => obs.observe(c));
    return () => obs.disconnect();
  }, []);

  // Feb 2026 — CTAs route to /pricing so visitors see the 3-tier
  // subscription selection (Solo / Team / Enterprise) before signing up.
  const goTrial = () => navigate("/pricing");

  return (
    <div className="landing-root" data-testid="landing-page">
      <svg className="grain" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <filter id="noiseFilter">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#noiseFilter)" />
      </svg>

      <nav className="nav" data-testid="landing-nav">
        <img className="logo-img" src={LOGO_SRC} alt="DH Design" data-testid="landing-logo" />
        <button className="trial-btn" onClick={goTrial} data-testid="landing-trial-btn-nav">START FREE TRIAL</button>
      </nav>

      <section className="hero" data-testid="landing-hero">
        <div className="corner-accent tl" />
        <div className="corner-accent br" />
        <div className="hero-content">
          <div className="eyebrow">AI SALES INTELLIGENCE · BUILT TO CLOSE</div>
          <h1 className="headline">
            Sales Intelligence that <em className="gold-gradient">will make your CRM cry like a little...</em>
            <span className="wink" role="img" aria-label="cry-wink">{"\u{1F62D}\u{1F609}"}</span>
          </h1>
          <p className="subhead">
            Your CRM stores data. <strong>DH Design hunts your deal, finds your buyer, builds your pitch, your proposal, and your entire funnel</strong> — then hands you a cold call script with every rebuttal already beat. You walk in already knowing how it ends — so you can help more people by closing with confidence.
          </p>
          <div className="cta-row">
            <button className="cta-primary" onClick={goTrial} data-testid="landing-hero-cta">START 14-DAY FREE TRIAL</button>
            <a className="cta-secondary" href="#capabilities">See how it works →</a>
          </div>
        </div>
      </section>

      <section className="closer-section" data-testid="landing-founder-section">
        <div className="closer-photo-bg">
          <img src={FOUNDER_SRC} alt="David Hughes, Founder" />
        </div>
        <p className="photo-caption">David Hughes <span>— Founder</span></p>
        <div className="closer-line">
          <p className="closer-lead">Oh — one more thing.</p>
          <ul className="closer-list">
            <li>Single or mass send your proposal, then watch every open and click land in real time.</li>
            <li>One click builds an executive forecast brief of your entire pipeline, ready to hand your team.</li>
            <li>Built-in retargeting reminds your prospect of your beautiful proposal — whether they open your email or not.</li>
            <li>Selling anything with a quantity? Drop in a service adjustor and let your buyer pick their number — right inside the proposal.</li>
            <li>Stack real stats and metrics into every funnel, because a proposal without proof is just a guess dressed up nice.</li>
            <li>Every proposal, every email — signed off with your beautiful digital contact card. One tap, and they{"'"}ve got you forever.</li>
          </ul>
          <p className="closer-attr">— The DH Design Promise</p>
        </div>
      </section>

      <div className="stats-strip" data-testid="landing-stats-strip">
        <span>22 LIVE SIGNALS PER SWEEP</span>
        <span className="dot">·</span>
        <span>10M FROM SIGNAL TO SENT PROPOSAL</span>
        <span className="dot">·</span>
        <span>100% FOCUSED ON YOUR TERRITORY</span>
      </div>

      <section className="capabilities" id="capabilities" data-testid="landing-capabilities">
        <div className="cap-eyebrow">EVERYTHING YOUR REPS NEED TO CLOSE</div>
        <h2 className="cap-title">One platform. <em>Not a CRM with AI bolted on —</em> a closing machine.</h2>
        <div className="cap-grid" ref={gridRef}>
          {CAPABILITIES.map((c) => (
            <div className="cap-card" key={c.name} data-testid={`cap-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="cap-icon" aria-hidden="true">{c.icon}</span>
              <div className="cap-name">{c.name}</div>
              <div className="cap-desc">{c.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="final-cta" data-testid="landing-final-cta">
        <p className="final-line">Your CRM stores data. <em>DH Design closes deals.</em></p>
        <button className="cta-primary" onClick={goTrial} data-testid="landing-final-cta-btn">START 14-DAY FREE TRIAL</button>
      </section>
    </div>
  );
}
